<template>
  <!-- 서비스소개 페이지 에디터 -->
  <div class="service-page-editor">
    <!-- 상단 헤더 -->
    <div class="service-page-editor__header">
      <h3 class="service-page-editor__title">서비스 페이지 관리</h3>

      <!-- 서비스 slug 선택 드롭다운 -->
      <a-select
        v-model:value="selectedSlug"
        style="width: 240px;"
        placeholder="서비스 선택"
        @change="handleSlugChange"
      >
        <a-select-option
          v-for="svc in serviceOptions"
          :key="svc.slug"
          :value="svc.slug"
        >
          {{ svc.label }}
        </a-select-option>
      </a-select>
    </div>

    <!-- 로딩 상태 -->
    <div v-if="loading" style="text-align: center; padding: 60px 0;">
      <a-spin size="large" tip="서비스 페이지를 불러오는 중..." />
    </div>

    <!-- 서비스 미선택 상태 -->
    <a-empty
      v-else-if="!selectedSlug"
      description="위에서 서비스를 선택하세요."
      style="margin-top: 60px;"
    />

    <!-- 선택된 서비스 데이터 없는 경우: 새 페이지 만들기 버튼 -->
    <div v-else-if="!serviceData" class="service-page-editor__empty">
      <a-empty description="이 서비스의 페이지가 없습니다.">
        <template #extra>
          <a-button
            type="primary"
            class="yellow-button"
            @click="handleCreateNew"
          >
            새 서비스 페이지 만들기
          </a-button>
        </template>
      </a-empty>
    </div>

    <!-- 섹션 카드 목록 -->
    <div v-else class="service-page-editor__sections">
      <!-- Hero 섹션 -->
      <a-card
        class="section-card"
        hoverable
        @click="handleEditSection('hero')"
      >
        <div class="section-card__header">
          <span class="section-card__title">히어로 배너</span>
          <edit-outlined class="section-card__icon" />
        </div>
        <div class="section-card__preview">
          <strong>{{ serviceData.hero?.title || '(제목 없음)' }}</strong>
          <p>{{ serviceData.hero?.subtitle || '(부제목 없음)' }}</p>
          <img
            v-if="serviceData.hero?.heroImage"
            :src="serviceData.hero.heroImage"
            class="section-card__thumb"
            alt="히어로 이미지"
          />
        </div>
      </a-card>

      <!-- Features 섹션 -->
      <a-card
        class="section-card"
        hoverable
        @click="handleEditSection('features')"
      >
        <div class="section-card__header">
          <span class="section-card__title">주요 기능</span>
          <edit-outlined class="section-card__icon" />
        </div>
        <div class="section-card__preview">
          <span>
            {{
              serviceData.features && serviceData.features.length
                ? `${serviceData.features.length}개 기능 등록`
                : '등록된 기능 없음'
            }}
          </span>
        </div>
      </a-card>

      <!-- Reviews 섹션 -->
      <a-card
        class="section-card"
        hoverable
        @click="handleEditSection('reviews')"
      >
        <div class="section-card__header">
          <span class="section-card__title">사용자 후기</span>
          <edit-outlined class="section-card__icon" />
        </div>
        <div class="section-card__preview">
          <span>
            {{
              serviceData.reviews && serviceData.reviews.length
                ? `${serviceData.reviews.length}개 후기 등록`
                : '등록된 후기 없음'
            }}
          </span>
        </div>
      </a-card>
    </div>
  </div>
</template>

<script>
import { message } from 'ant-design-vue';
import { mapMutations } from 'vuex';
import { EditOutlined } from '@ant-design/icons-vue';
import { HomepageApi } from '../api';

// 서비스 slug → 레이블 매핑
const SERVICE_OPTIONS = [
  { slug: 'certificate', label: '정품 인증서' },
  { slug: 'ai-chatbot', label: 'AI 상담봇' },
  { slug: 'crm-messaging', label: 'CRM 메시징' },
  { slug: 'event-marketing', label: '이벤트 마케팅' },
];

export default {
  name: 'ServicePageEditor',

  components: {
    EditOutlined,
  },

  data() {
    return {
      // 선택된 서비스 slug
      selectedSlug: null,
      // 현재 선택된 서비스 데이터
      serviceData: null,
      // 로딩 상태
      loading: false,
      // 서비스 옵션 목록
      serviceOptions: SERVICE_OPTIONS,
    };
  },

  methods: {
    ...mapMutations('homepageEditor', ['SET_ACTIVE_EDITOR']),

    // slug 선택 변경 시 해당 서비스 페이지 데이터 로드
    async handleSlugChange(slug) {
      this.serviceData = null;
      if (!slug) return;

      this.loading = true;
      try {
        const res = await HomepageApi.getServicePage(slug);
        this.serviceData = res.data || null;
      } catch (error) {
        // 404이면 데이터 없음 상태 유지, 그 외는 에러 메시지
        if (error.response?.status === 404) {
          this.serviceData = null;
        } else {
          message.error('서비스 페이지를 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        this.loading = false;
      }
    },

    // 새 서비스 페이지 생성 (빈 upsert)
    async handleCreateNew() {
      if (!this.selectedSlug) return;

      this.loading = true;
      try {
        const emptyPage = {
          slug: this.selectedSlug,
          hero: { title: '', subtitle: '', heroImage: '', ctaText: '', ctaUrl: '' },
          features: [],
          reviews: [],
        };
        await HomepageApi.upsertServicePage(emptyPage);
        this.serviceData = emptyPage;
        message.success('서비스 페이지가 생성되었습니다.');
      } catch (error) {
        message.error('서비스 페이지 생성 중 오류가 발생했습니다.');
      } finally {
        this.loading = false;
      }
    },

    // 섹션 편집 드로어 열기
    handleEditSection(section) {
      this.SET_ACTIVE_EDITOR({
        type: 'drawer',
        sectionKey: 'serviceSection',
        payload: {
          section,
          slug: this.selectedSlug,
          data: this.serviceData,
        },
      });
    },

    // 외부에서 서비스 데이터 갱신 (ServiceSectionEditor 저장 후 호출)
    async refreshServiceData() {
      if (!this.selectedSlug) return;
      await this.handleSlugChange(this.selectedSlug);
    },
  },
};
</script>

<style scoped>
.service-page-editor__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.service-page-editor__title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
  margin: 0;
}

.service-page-editor__empty {
  margin-top: 60px;
  text-align: center;
}

.service-page-editor__sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-card {
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.section-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.section-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.section-card__title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.section-card__icon {
  color: #aaa;
}

.section-card__preview {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.section-card__preview p {
  margin: 4px 0 8px;
  color: #888;
}

.section-card__thumb {
  max-height: 80px;
  border-radius: 4px;
  border: 1px solid #eee;
  display: block;
  margin-top: 8px;
}
</style>
